<script setup lang="ts">
import { FAB_ORDER } from './fab-action.constants'

/**
 * Floating action button. Teleports into the global #fab-slot in app.vue,
 * which arranges all FabActions in a bottom-right row via flex-row-reverse.
 *
 * Use the FAB_ORDER tokens for placement (page-level → corner; global widgets
 * stack to the left). Pass a #popover slot to wrap the button in a UPopover —
 * the slot's content becomes the popover's #panel.
 *
 * Sizing and shape are fixed (size="xl", rounded-full, shadow-lg) so all
 * floating actions share the same visual register. UButton attrs/listeners
 * pass through unchanged.
 */

interface Props {
  order?: number
  /** Extra classes for the icon — used e.g. for animate-spin on a busy state. */
  iconClass?: string
}

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<Props>(), {
  order: FAB_ORDER.page,
  iconClass: '',
})

const slots = defineSlots<{
  default?: () => unknown
  popover?: () => unknown
}>()

const hasPopover = computed(() => !!slots.popover)

const buttonUi = computed(() => ({
  rounded: 'rounded-full',
  icon: {
    size: { xl: 'h-5 w-5' },
    base: props.iconClass,
  },
}))
</script>

<template>
  <Teleport defer to="#fab-slot">
    <div class="pointer-events-auto" :style="{ order }">
      <UPopover
        v-if="hasPopover"
        :popper="{ placement: 'top-end', offsetDistance: 12 }"
      >
        <UButton
          size="xl"
          class="shadow-lg transition-transform duration-150 ease-out active:scale-[0.97]"
          :ui="buttonUi"
          v-bind="$attrs"
        >
          <slot />
        </UButton>
        <template #panel>
          <slot name="popover" />
        </template>
      </UPopover>
      <UButton
        v-else
        size="xl"
        class="shadow-lg transition-transform duration-150 ease-out active:scale-[0.97]"
        :ui="buttonUi"
        v-bind="$attrs"
      >
        <slot />
      </UButton>
    </div>
  </Teleport>
</template>
